import axios from 'axios'
import {
    UseMutationOptions,
    UseMutationResult,
    UseQueryOptions,
    useMutation,
    useQuery,
    useQueryClient,
} from 'react-query'
import { ApiError } from './common'

export interface Profile {
    name: string
    lastName: string
    data: {
        [index: string]: string | number | boolean | object | null | undefined
    }
}
export interface User {
    userId: string
    email: string
    profile: Profile
    created: number
    access: {
        chatAPIs: boolean
        dataApps: boolean
        internalUserId: number
        webAPIs: boolean
        workspaceId: number
    } | null
    selections: { selectedModule: string } | null
}

export interface UserAuth extends User {
    token: string
    isNew: boolean
}

export interface AuthOptions {
    email: string
    password: string
}

export interface PasswordChangeOptions {
    email: string
}

export interface PasswordSaveOptions {
    email: string
    code: string
    password: string
}

export const getUserModel = async (): Promise<User> => {
    const { data } = await axios.get<User>(
        `${process.env.REACT_APP_ACCOUNTS_API_HOST}/v1/users/me`
    )
    return data
}

export const useUser = (options?: UseQueryOptions<User, ApiError>) => {
    return useQuery<User, ApiError>('user', getUserModel, options)
}

export const authorizeUser = async (
    options: AuthOptions
): Promise<UserAuth> => {
    const { data } = await axios.post<UserAuth>(
        `${process.env.REACT_APP_ACCOUNTS_API_HOST}/v1/users/login`,
        options
    )
    return data
}

export const createUser = async (options: AuthOptions): Promise<UserAuth> => {
    const { data } = await axios.post<UserAuth>(
        `${process.env.REACT_APP_ACCOUNTS_API_HOST}/v1/users`,
        options
    )
    return data
}

export const updateProfile = async (profile: Profile): Promise<User> => {
    const { data } = await axios.post<User>(
        `${process.env.REACT_APP_ACCOUNTS_API_HOST}/v1/users/me/profile`,
        profile
    )
    return data
}

export const startPasswordChange = async (
    options: PasswordChangeOptions
): Promise<any> => {
    const { data } = await axios.post(
        `${process.env.REACT_APP_ACCOUNTS_API_HOST}/v1/users/password/code`,
        options
    )
    return data
}

export const changePassword = async (
    options: PasswordSaveOptions
): Promise<any> => {
    const { data } = await axios.post(
        `${process.env.REACT_APP_ACCOUNTS_API_HOST}/v1/users/password/change`,
        options
    )
    return data
}

export const changeSelectedModule = async (options: any): Promise<any> => {
    const { data } = await axios.post(
        `${process.env.REACT_APP_ACCOUNTS_API_HOST}/v1/users/me/selections`,
        options
    )
    return data
}

export const useSelectedModule = (): UseMutationResult<any, ApiError, any> => {
    const queryClient = useQueryClient()
    return useMutation<any, ApiError, any>({
        mutationKey: ['selectedModule'],
        mutationFn: (params) => changeSelectedModule(params),
        onSuccess: () => {
            queryClient.invalidateQueries(['user'])
        },
        // ...options,
    })
}
